import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Info } from "lucide-react";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";

const defaultSettings = {
  useCommissions: false,
  commissionValue: "0",
  customizeBreakeven: false,
  breakEvenThreshold: 2,
  omitBreakeven: false,
  useFundedBalance: false,
  fundedBalance: "25000",
  fundedAccountCount: 1, // New setting for account count
  enableClock: false,
  selectedTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Add default timezone
};

const MetricSettings = forwardRef((props, ref) => {
  const [settings, setSettings] = useState(defaultSettings);
  const { user } = useAuth();

  // Funded account balance options
  const balanceOptions = Array.from({ length: 10 }, (_, i) => (i + 1) * 25000);

  // Account count options
  const accountCountOptions = Array.from({ length: 10 }, (_, i) => i + 1);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) return;
      try {
        const settingsRef = doc(db, "userSettings", user.uid);
        const settingsSnap = await getDoc(settingsRef);

        if (settingsSnap.exists()) {
          const storedSettings = settingsSnap.data();
          console.log("Loaded settings from Firestore:", storedSettings);
          setSettings({
            ...defaultSettings,
            ...storedSettings,
          });
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    fetchSettings();
  }, [user]);

  // Expose getSettings method to parent
  useImperativeHandle(ref, () => ({
    getSettings: () => {
      console.log("Returning settings to parent:", settings);
      return settings;
    },
  }));

  const handleSettingChange = (setting, value) => {
    console.log(`Changing setting: ${setting} to:`, value);
    setSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  return (
    <div className="settings-content">
      <div className="setting-item">
        <div className="setting-info">
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id="useCommissions"
              checked={settings.useCommissions}
              onChange={(e) =>
                handleSettingChange("useCommissions", e.target.checked)
              }
            />
            <label htmlFor="useCommissions">Include Commissions</label>
          </div>
          {settings.useCommissions && (
            <div className="commission-input">
              <input
                type="number"
                step="0.01"
                value={settings.commissionValue || "0"}
                onChange={(e) =>
                  handleSettingChange("commissionValue", e.target.value)
                }
                placeholder="Commission per contract/share"
              />
            </div>
          )}
          <p>
            Add commission costs to trade calculations (applied to both entry
            and exit)
          </p>
        </div>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id="omitBreakeven"
              checked={settings.omitBreakeven || false}
              onChange={(e) =>
                handleSettingChange("omitBreakeven", e.target.checked)
              }
            />
            <label htmlFor="omitBreakeven">Omit Breakeven Trades</label>
          </div>
          <p>Exclude breakeven trades from metrics calculations</p>
        </div>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id="customizeBreakeven"
              checked={settings.customizeBreakeven || false}
              onChange={(e) =>
                handleSettingChange("customizeBreakeven", e.target.checked)
              }
            />
            <label htmlFor="customizeBreakeven">
              Customize Breakeven Threshold
            </label>
          </div>
          {settings.customizeBreakeven && (
            <div className="breakeven-slider">
              <div className="slider-header">
                <span>
                  Breakeven Range: ±{settings.breakEvenThreshold ?? 2}%
                </span>
                <Info
                  size={16}
                  className="info-icon"
                  title="Trades within this range will be considered breakeven"
                />
              </div>
              <input
                type="range"
                min="0"
                max="11"
                step="1"
                value={settings.breakEvenThreshold ?? 2}
                onChange={(e) =>
                  handleSettingChange(
                    "breakEvenThreshold",
                    parseInt(e.target.value)
                  )
                }
                className="range-slider"
              />
              <div className="slider-labels">
                <span>±0%</span>
                <span>±11%</span>
              </div>
            </div>
          )}
          <p>Adjust the percentage range that defines a breakeven trade</p>
        </div>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id="useFundedBalance"
              checked={settings.useFundedBalance || false}
              onChange={(e) =>
                handleSettingChange("useFundedBalance", e.target.checked)
              }
            />
            <label htmlFor="useFundedBalance">
              Funded Futures Account Balance
            </label>
          </div>
          {settings.useFundedBalance && (
            <>
              <div className="balance-select">
                <label htmlFor="fundedBalance">Account Size</label>
                <select
                  id="fundedBalance"
                  value={settings.fundedBalance || "25000"}
                  onChange={(e) =>
                    handleSettingChange("fundedBalance", e.target.value)
                  }
                >
                  {balanceOptions.map((amount) => (
                    <option key={amount} value={amount}>
                      ${amount.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="balance-select">
                <label htmlFor="fundedAccountCount">Number of Accounts</label>
                <select
                  id="fundedAccountCount"
                  value={settings.fundedAccountCount || 1}
                  onChange={(e) =>
                    handleSettingChange(
                      "fundedAccountCount",
                      parseInt(e.target.value)
                    )
                  }
                >
                  {accountCountOptions.map((count) => (
                    <option key={count} value={count}>
                      {count} {count === 1 ? "Account" : "Accounts"}
                    </option>
                  ))}
                </select>

                <div
                  style={{
                    marginTop: "0.5rem",
                    color: "var(--text-secondary)",
                    fontSize: "0.85rem",
                  }}
                >
                  Total Balance: $
                  {(
                    parseInt(settings.fundedBalance || 25000) *
                    (settings.fundedAccountCount || 1)
                  ).toLocaleString()}
                </div>
              </div>
            </>
          )}
          <p>
            Set account balance for funded futures accounts and multiply across
            multiple accounts
          </p>
        </div>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id="enableClock"
              checked={settings.enableClock || false}
              onChange={(e) =>
                handleSettingChange("enableClock", e.target.checked)
              }
            />
            <label htmlFor="enableClock">Enable Clock</label>
          </div>
          {settings.enableClock && (
            <div className="timezone-select-container">
              <select
                value={settings.selectedTimezone}
                onChange={(e) =>
                  handleSettingChange("selectedTimezone", e.target.value)
                }
                className="settings-select"
              >
                <option
                  value={Intl.DateTimeFormat().resolvedOptions().timeZone}
                >
                  Local Time
                </option>
                <option value="America/New_York">New York</option>
                <option value="America/Chicago">Chicago</option>
                <option value="America/Denver">Denver</option>
                <option value="America/Los_Angeles">Los Angeles</option>
                <option value="Europe/London">London</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </select>
            </div>
          )}
          <p>Display a real-time clock with timezone selection</p>
        </div>
      </div>
    </div>
  );
});

MetricSettings.displayName = "MetricSettings";

export default MetricSettings;
